import { AccrualSuggestionBucket } from "@common/types/accrualSuggestionBucket";
import { TransactionStatus } from "@common/types/transaction";
import {
    TransactionColumn,
    TransactionView,
} from "@common/types/views/transactionViews";
import { ViewType } from "@common/types/views/views";

/**
 * Columns displayed on the Accrual review page and on the accrual-review
 * system tasks (one per bucket). Defined in `common/` so both the frontend
 * default view and the backend task-view persistence can use the same shape.
 */
export const ACCRUAL_REVIEW_COLUMNS: TransactionView["columns"] = [
    {
        id: TransactionColumn.LOCK,
        options: { pin: "left" },
    },
    {
        id: TransactionColumn.DATE,
        options: { pin: "left" },
    },
    {
        id: TransactionColumn.SPLIT,
        options: { pin: "left" },
    },
    {
        id: TransactionColumn.BANK_DESCRIPTION,
    },
    {
        id: TransactionColumn.COUNTERPARTY,
    },
    {
        id: TransactionColumn.ACCOUNT,
    },
    {
        id: TransactionColumn.AMOUNT,
    },
    {
        id: TransactionColumn.ENTITY,
    },
    {
        id: TransactionColumn.ACCRUAL_MATCH,
        options: { pin: "right" },
    },
    {
        id: TransactionColumn.ACCRUAL_REVIEW_LEDGER,
        options: { pin: "right" },
    },
    {
        id: TransactionColumn.ACCRUAL_ACTIONS,
        options: { pin: "right" },
    },
];

export const ACCRUAL_REVIEW_VIEW_OPTIONS: TransactionView["options"] = {
    bulkActions: true,
    selection: true,
};

export function createAccrualReviewDefaultView(
    entityIds: number[],
): TransactionView {
    return {
        type: ViewType.TRANSACTION,
        options: ACCRUAL_REVIEW_VIEW_OPTIONS,
        columns: ACCRUAL_REVIEW_COLUMNS,
        filters: {
            entityIds,
            isNotAccrualReviewed: true,
            statuses: [TransactionStatus.COMPLETED],
        },
    };
}

/**
 * Accrual-review columns without the ACCRUAL_MATCH column, used by buckets
 * that never have an `accrual_match_suggestion` row to display.
 */
export const ACCRUAL_REVIEW_COLUMNS_WITHOUT_MATCH: TransactionView["columns"] =
    ACCRUAL_REVIEW_COLUMNS.filter(
        (column) => column.id !== TransactionColumn.ACCRUAL_MATCH,
    );

/**
 * Both bucket predicates require that no `accrual_match_suggestion` row
 * references the transaction — that is the whole definition of NONE, and
 * RULE_SUGGESTED holds only the rule-suggested transactions that nothing else
 * matched. The match column would therefore always be empty, so hide it for
 * those buckets. Users can still open the match drawer via the "Match" option
 * in the action-column kebab.
 */
const BUCKETS_WITHOUT_MATCH_COLUMN: ReadonlySet<AccrualSuggestionBucket> =
    new Set([
        AccrualSuggestionBucket.NONE,
        AccrualSuggestionBucket.RULE_SUGGESTED,
    ]);

/**
 * Returns the accrual-review column list for a bucket, hiding the
 * ACCRUAL_MATCH column for buckets that never have a match suggestion to show.
 * Shared by the backend task-view persistence and the frontend close-template
 * task view config so the two cannot drift.
 */
export function columnsForBucket(
    bucket: AccrualSuggestionBucket,
): TransactionView["columns"] {
    return BUCKETS_WITHOUT_MATCH_COLUMN.has(bucket)
        ? ACCRUAL_REVIEW_COLUMNS_WITHOUT_MATCH
        : ACCRUAL_REVIEW_COLUMNS;
}
